import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { connectSocket, disconnectSocket } from '@/services/socket';

/**
 * Manages WebSocket connection at app level.
 * Always connects (for guests to view canvas).
 * Reconnects with auth token when user logs in.
 * Stays connected across route changes.
 *
 * We intentionally do NOT disconnect in effect cleanup. React Strict Mode
 * mounts → unmounts → remounts; disconnecting on cleanup closed the socket
 * before it was established and caused "WebSocket is closed before the
 * connection is established". We only disconnect when reconnecting for
 * login/logout. On page close/refresh the tab is gone anyway.
 */
export function useSocketConnection() {
  // Only depend on user ID, not the entire user object
  // This prevents reconnects when user properties change (like inkBalance)
  const userId = useAuthStore((state) => state.user?.id);
  const previousUserId = useRef<string | undefined>(undefined);
  const isConnected = useRef(false);

  useEffect(() => {
    const userChanged = previousUserId.current !== userId;

    if (!isConnected.current) {
      // Initial connection (guest or authenticated)
      connectSocket();
      isConnected.current = true;
    } else if (userChanged && userId) {
      // User just logged in - reconnect with auth token
      disconnectSocket();
      connectSocket();
    } else if (userChanged && !userId && previousUserId.current) {
      // User just logged out - reconnect as guest
      disconnectSocket();
      connectSocket();
    }

    previousUserId.current = userId;

    // No cleanup disconnect: avoids closing the socket during Strict Mode
    // unmount, which caused "WebSocket is closed before connection is established".
  }, [userId]);
}
